import { useState, useContext, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { makeRequest } from "../../../utils/axios.js"; 
import { AuthContext } from "../../../context/authContext";
import { buildCommentTree } from "../utils/commentTree";
import { useMemo } from "react";

export const useComments = (postId) => {
    const [desc, setDesc] = useState("");
    const [cooldown, setCooldown] = useState(0); 
    const [isFetchingPreview, setIsFetchingPreview] = useState(false);
    const { currentUser } = useContext(AuthContext);
    const queryClient = useQueryClient();

    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);

    const { isLoading, error, data } = useQuery({
        queryKey: ["comments", postId],
        queryFn: () => makeRequest.get("/comments?postId=" + postId).then((res) => res.data),
    });

    const mutation = useMutation({
        mutationFn: (newComment) => makeRequest.post("/comments", newComment),
        onSuccess: () => {
            setDesc(""); 
            queryClient.invalidateQueries({ queryKey: ["comments", postId] });
        },
        onError: (err) => {
            const status = err.response?.status;
            if (status === 429) {
                alert("You're commenting too quickly. Please slow down!");
                setCooldown(15); 
            } else if (status === 409) {
                alert("You've just posted a similar comment!");
                setCooldown(5); 
            } else {
                console.error("Comment submission error:", err);
            }
        }
    });

    const handleClick = async (e) => {
        e.preventDefault();
        if (desc.trim() === "" || cooldown > 0 || mutation.isPending || isFetchingPreview) return;

        setIsFetchingPreview(true);
        let linkPayload = {};

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = desc.match(urlRegex);
        const url = matches ? matches[0] : null;

        if (url) {
            try {
                const res = await makeRequest.post("/posts/preview", { url }); 
                if (res.data) {
                    linkPayload = {
                        linkUrl: res.data.url || url,
                        linkTitle: res.data.title,
                        linkDesc: res.data.description,
                        linkImg: res.data.image || res.data.img 
                    };
                }
            } catch (err) {
                console.error("Comment submission error:", err);
                linkPayload = { linkUrl: url };
            }
        }

        mutation.mutate({ 
            desc, 
            postId, 
            parentCommentId: null,
            logicType: "ANSWER",
            ...linkPayload 
        });
        
        setIsFetchingPreview(false);
    };

    const treeComments = useMemo(() => {
        if (!data) return [];
        return buildCommentTree(data);
    }, [data]);

    return {
        currentUser,
        desc,
        setDesc,
        handleClick,
        isLoading,
        error,
        rootComments: treeComments,      
        isSending: mutation.isPending || isFetchingPreview,
        cooldown,
        queryClient             
    };
};